import { IkeUnitType, type MarkerInsightState, MarkerType } from '@ikegps/core-api';
import type { Point } from '@ikegps/core-api';
import { action, computed, makeObservable, observable } from 'mobx';
import type { SVGRenderOptions } from '../../../draw';
import { IkeId } from '../../../id';
import { Vector } from '../../../math/vector';
import { IkeUnit } from '../../../unit';
import {
	type ApprovableInsightDrawable,
	CollectionModelDrawable,
	type DrawableWithLabel,
	InsightMarkerDetectionClass,
	isInsightOffsetDetectionClass as isInsightBaseOffsetDetectionClass,
	MoveableDrawable,
} from './drawable';
import { CollectionModelDrawableState } from './drawable.state';
import { CollectionModelDrawableLabel } from './label.drawable';
import { CollectionModelDrawableMarkerOffset } from './marker.offset.drawable';

export class CollectionModelDrawableMarker
	extends MoveableDrawable
	implements DrawableWithLabel, ApprovableInsightDrawable
{
	static createWithLabel(
		drawableState: CollectionModelDrawableState,
		markerPoint: Point,
		labelPoint: Point,
		id: string = IkeId.generate(),
		type = MarkerType.NONE,
		insight?: MarkerInsightState
	) {
		const labelId = `${id}:label`;

		const marker = new CollectionModelDrawableMarker(drawableState, id, markerPoint.y, labelId, type, insight);

		const label = new CollectionModelDrawableLabel(
			drawableState,
			labelId,
			labelPoint.x,
			labelPoint.y,
			marker.id,
			undefined,
			undefined
		);

		return { marker, label };
	}

	constructor(
		collectionModelDrawableState: CollectionModelDrawableState,
		drawableId: string,
		y: number,
		labelId: string,
		type: MarkerType = MarkerType.NONE,
		insight?: MarkerInsightState,
		pair?: string | string[],
		offset: number = 0
	) {
		super(collectionModelDrawableState, drawableId);
		makeObservable(this);

		this.y = y;
		this._labelId = labelId;
		this.type = type;
		this.insight = insight;
		this._pair = pair;
		this._offset = offset;
	}

	override get x() {
		// Markers must always be in the center of the image
		if (this.drawableState.capture != null) {
			return this.drawableState.capture.resolution.width / 2;
		}
		return 0;
	}

	@observable override y: number = 0;

	/**
	 * Move the marker to this location
	 *
	 * **Markers can only be moved vertically**
	 *
	 * @param x coordinates in pixels
	 * @param y coordinates in pixels
	 */
	@action override move(x: number, y: number) {
		const bounds = this.getPointInBounds(this.x, y);
		this.y = bounds.y;
	}

	@action moveNoBounds(x: number, y: number) {
		this.y = y;
	}

	protected _labelId: string;

	get label(): CollectionModelDrawableLabel {
		return this.drawableState.getDrawable(this._labelId);
	}

	getLabelConnectionPoint(options: SVGRenderOptions): Point {
		return {
			x: this.label.isOnLeftSide()
				? this.x - options.draw.state.markerOffsetWidth // Left
				: this.x + options.draw.state.markerOffsetWidth, // Right
			y: this.y,
		};
	}

	@observable public type: MarkerType | null = null;

	protected _offset: number;

	get offset(): number {
		return this._offset;
	}

	set offset(offset: number) {
		if (!CollectionModelDrawableMarker.isBase(this)) {
			throw new Error('Cannot set offset on non base marker');
		}

		if (offset < 0) {
			throw new Error(`Offset cannot be a negative number; got: ${offset}`);
		}

		const baseOffset = this.drawableState.baseOffset;

		if (!offset) {
			// Remove old base offset marker
			baseOffset?.remove();
			this._offset = 0;
			return;
		}

		this._offset = offset;

		// Create new base offset marker if it doesn't exist
		if (baseOffset == null) {
			CollectionModelDrawableMarkerOffset.createWithLabel(this.drawableState);
		}
	}

	protected _pair?: string | string[];

	/**
	 * Pair links two markers together -- it is used by when linked to vertical separation fields.
	 */
	get pair(): string | string[] | undefined {
		return this._pair;
	}

	set pair(value: string | string[] | undefined) {
		this._pair = value;
	}

	/**
	 * Remove a specific marker ID from the `_pair` array.
	 * Sets `_pair = undefined` if the array becomes empty.
	 */
	clearPairReference(otherMarkerId: string) {
		const pairs = this.pairArray.filter((id) => id !== otherMarkerId);
		this._pair = pairs.length > 0 ? pairs : undefined;
	}

	@observable insight?: MarkerInsightState = undefined;

	@computed override get isInsightMeasurement() {
		// True when insight state exists and is not applied.
		// False when insight state doesn't exist, or it exists but is applied.
		return this.insight ? !this.insight?.applied : false;
	}

	override isApprovableInsightDrawable(): this is ApprovableInsightDrawable {
		return true;
	}

	override get isVisible() {
		if (this.isInsightMeasurement) {
			// Show applied insight drawables
			if (this.insight?.applied) {
				return true;
			}
			// If the image already has a base, then hide unapplied insight base/offset markers
			if (this.collectionHasBase) {
				return !this.isInsightBaseOffsetDetectionClass;
			}
		}
		return true;
	}

	/**
	 * Shortcut to check if the insight detection class is a base offset marker.
	 */
	get isInsightBaseOffsetDetectionClass() {
		if (!this.insight?.detection_class) {
			return false;
		}
		return isInsightBaseOffsetDetectionClass(this.insight.detection_class);
	}

	@action approveInsightFlag() {
		if (!this.insight || this.insight.applied) {
			return;
		}

		this.insight.applied = true;

		// If approving a base offset detection, set the offset value.
		if (this.isInsightBaseOffsetDetectionClass) {
			if (this.drawableState.base) {
				throw new Error('Attempted to approve a base offset but a base already exists');
			}

			let offsetValue = 0;
			switch (this.insight.detection_class) {
				case InsightMarkerDetectionClass.NineFeetMarker:
					offsetValue = 9;
					break;
				case InsightMarkerDetectionClass.SixFeetMarker:
					offsetValue = 6;
					break;
				case InsightMarkerDetectionClass.ThreeFeetMarker:
					offsetValue = 3;
					break;
				default:
					break;
			}

			this.type = MarkerType.BASE;

			this.offset = IkeUnit.convert(offsetValue, IkeUnit.METER, IkeUnit.FEET, IkeUnitType.DISTANCE);
		}
	}

	static isMarker(t: CollectionModelDrawable): t is CollectionModelDrawableMarker {
		return t instanceof CollectionModelDrawableMarker;
	}

	static isBase(t: CollectionModelDrawable): t is CollectionModelDrawableMarker {
		return CollectionModelDrawableMarker.isMarker(t) && t.type === MarkerType.BASE;
	}

	/** Is this marker just a standard marker */
	static isNone(t: CollectionModelDrawable): t is CollectionModelDrawableMarker {
		return CollectionModelDrawableMarker.isMarker(t) && t.type === MarkerType.NONE;
	}

	// TODO: Rename this to something easier to understand, like "offset" or "height"
	get value(): number | null {
		if (this.type === MarkerType.BASE) {
			return this.offset || 0;
		}
		if (this.type === MarkerType.BASE_OFFSET) {
			return 0;
		}

		return this.calculateValue();
	}

	override remove() {
		// Remove this marker's label too, unless it has already been removed
		if (this.drawableState.hasDrawable(this._labelId)) {
			this.label.remove();
		}
		super.remove();
	}

	public calculateM2Px(heightMeters: number) {
		// if a base offset exists, we should use that measurement instead of the base
		const baseMarker = this.drawableState.baseOffset || this.drawableState.base;

		if (baseMarker == null) {
			return null;
		}

		const baseMarkerXY = baseMarker.getVector();
		const moveVector = new Vector(0, -heightMeters);

		const moveDistance = this.drawableState.collection.state
			.getGeometry(this.captureId)
			.movePoint(baseMarkerXY, moveVector);

		return moveDistance;
	}

	/**
	 * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
	 */
	private calculateValue() {
		const baseMarker = this.drawableState.base;
		if (baseMarker == null) {
			return null;
		}

		const adjustOffset = baseMarker.offset || 0;

		const baseMarkerXY = baseMarker.getVector();
		const thisXY = this.getVector();

		const distance = this.drawableState.collection.state
			.getGeometry(this.captureId)
			.calculateDistance(baseMarkerXY, thisXY);

		if (baseMarkerXY.y < thisXY.y) {
			return adjustOffset - distance;
		}

		return adjustOffset + distance;
	}

	private verticalDistanceFromLaser() {
		// laser point is assumed to be in the center of the image
		const capture = this.drawableState.capture;
		if (!capture) {
			return null;
		}
		const centerHeight = capture.resolution.height / 2;
		const markerHeight = this.getVector().y;
		const distance = this.drawableState.collection.state
			.getGeometry(this.captureId)
			.calculateWorldDifferenceVector(new Vector(0, markerHeight), new Vector(0, centerHeight)).y;
		return distance;
	}

	get altitude() {
		return this.drawableState.capture?.target.altitude ?? 0 + (this.verticalDistanceFromLaser() ?? 0);
	}

	override get isDeletable() {
		return this.type === MarkerType.NONE;
	}

	override get isMarker(): boolean {
		return true;
	}

	override get isBase(): boolean {
		return this.type === MarkerType.BASE;
	}

	override clone(newParentDrawableState: CollectionModelDrawableState): CollectionModelDrawableMarker {
		if (!newParentDrawableState.hasDrawable(this._labelId)) {
			this.label.clone(newParentDrawableState);
		}
		return new CollectionModelDrawableMarker(
			newParentDrawableState,
			this.id,
			this.y,
			this._labelId,
			this.type,
			this.insight ? { ...this.insight } : undefined,
			this.pair,
			this._offset
		);
	}

	protected get pairArray() {
		return typeof this._pair === 'string' ? [this._pair] : (this._pair ?? []);
	}

	override equals(other: CollectionModelDrawable): boolean {
		return (
			other instanceof CollectionModelDrawableMarker &&
			this.type === other.type &&
			this.y === other.y &&
			this._labelId === other._labelId &&
			this._offset === other._offset &&
			this.pairArray.join(',') === other.pairArray.join(',') &&
			this.insight?.applied === other.insight?.applied &&
			this.insight?.detection_class === other.insight?.detection_class &&
			this.insight?.detection_id === other.insight?.detection_id &&
			this.insight?.detection_version === other.insight?.detection_version &&
			this._equals(other)
		);
	}
}
